/**
 * The served analysis, and starting the run that produces it.
 *
 * One query for the whole tab: the counts, the verdicts and every document's
 * findings come in one piece, so the screen never draws a verdict from one
 * read beside a headline from another. The run's motion rides the task's
 * stream and is patched into this query by ``useFileProcessing``.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdmissionRuling,
  AnalysisResponse,
} from "@patent-studio/api-types";
import { readAnalysis, ruleAdmission, startRun } from "@/services/novelty";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";
import {
  noveltyAnalysisKey,
  positionsKey,
  thesisGateScopes,
} from "./query-keys";

export function useNoveltyAnalysis(taskId: string) {
  return useQuery({
    queryKey: noveltyAnalysisKey(taskId),
    queryFn: () => readAnalysis(taskId),
  });
}

/**
 * Start a run: every position, or the ones named. Where the analysis is
 * already in the cache, the accepted state is patched into it so the tab
 * reads as running before the stream's first event. The gate is read again
 * either way, since a refusal names the conditions the gate lists and an
 * acceptance moves the run's state on it.
 */
export function useStartRun(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.noveltyRunStart,
    mutationFn: (positionIds?: string[]) => startRun(taskId, positionIds),
    onSuccess: async (accepted) => {
      const key = noveltyAnalysisKey(taskId);
      // A read in flight would land after this patch and put the pre-start
      // status back until the stream's first event; it is stopped first.
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<AnalysisResponse>(key, (prev) =>
        prev
          ? {
              ...prev,
              status: accepted.status,
              run_id: accepted.run_id,
              error: null,
            }
          : prev,
      );
    },
    onSettled: () =>
      Promise.all(
        thesisGateScopes(taskId).map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

/**
 * Accept or strike one entry of what the disclosure admits. The analysis the
 * server answers is put in the cache as it is, since striking an entry moves
 * the verdicts and strips it read; the gates and the positions are read again,
 * since the thesis address moves with them and a position's confirmation is
 * read from its strip.
 */
export function useRuleAdmission(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.noveltyAdmissionRule,
    mutationFn: ({
      entryId,
      ruling,
    }: {
      entryId: string;
      ruling: AdmissionRuling;
    }) => ruleAdmission(taskId, entryId, ruling),
    onSuccess: async (analysis) => {
      const key = noveltyAnalysisKey(taskId);
      // A read in flight was sent before the ruling and would land after it
      // with the entry as it stood; it is stopped first.
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<AnalysisResponse>(key, analysis);
    },
    onSettled: () =>
      Promise.all(
        [...thesisGateScopes(taskId), positionsKey(taskId)].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}
